import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';
import { db } from '../db';

export type ToastType = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  visible: boolean;
}

export interface NotificationItem {
  id: number;
  message: string;
  type: ToastType;
  timestamp: number;
  read: boolean;
  // İleride bildirimi ilgili kayda (örn. gider/ödeme) bağlamak için — şimdilik hiçbir yerde set edilmiyor.
  relatedId?: string;
  relatedType?: string;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType, related?: { id: string; type: string }) => void;
  notifications: NotificationItem[];
  unreadCount: number;
  markAllRead: () => void;
  clearNotificationsByRelated: (relatedType: string, relatedId: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const BG: Record<ToastType, string> = {
  success: 'bg-green-600',
  error:   'bg-red-500',
  info:    'bg-orange-500',
};

const ICON: Record<ToastType, string> = {
  success: 'check_circle',
  error:   'error',
  info:    'info',
};

// Date.now() aynı milisaniyede birden fazla çağrıldığında (örn. birden fazla kritik
// stok kalemi aynı anda bildirim oluşturunca) çakışabiliyor - bu sayaç her çağrıda
// benzersiz bir id garanti eder (Dexie birincil anahtar çakışmasını ve React'in
// "aynı key" hatasını önler).
let notificationIdCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  // Uygulama açılışında kalıcı bildirimleri Dexie'den yükle (en yeni 50)
  useEffect(() => {
    db.notifications.orderBy('timestamp').reverse().limit(50).toArray()
      .then(rows => setNotifications(rows as NotificationItem[]))
      .catch(e => console.error('Bildirimler yüklenemedi:', e));
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'info', related?: { id: string; type: string }) => {
    const id = Date.now() * 1000 + (notificationIdCounter++ % 1000);
    const newNotification: NotificationItem = { id, message, type, timestamp: Date.now(), read: false, relatedId: related?.id, relatedType: related?.type };
    setToasts(prev => [...prev, { id, message, type, visible: true }]);
    setNotifications(prev => [newNotification, ...prev].slice(0, 50));

    // Kalıcı kayıt (fire-and-forget) - en eski 50'den fazlasını da budar
    db.notifications.add(newNotification)
      .then(() => db.notifications.count())
      .then(async (count) => {
        if (count > 50) {
          const oldest = await db.notifications.orderBy('timestamp').limit(count - 50).toArray();
          await db.notifications.bulkDelete(oldest.map((n: any) => n.id));
        }
      })
      .catch(e => console.error('Bildirim kaydedilemedi:', e));

    setTimeout(() => {
      setToasts(prev => prev.map(t => t.id === id ? { ...t, visible: false } : t));
    }, 3000);

    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3500);
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => n.read ? n : { ...n, read: true }));
    db.notifications.toCollection().modify({ read: true }).catch(e => console.error('Bildirimler okundu işaretlenemedi:', e));
  }, []);

  const clearNotificationsByRelated = useCallback((relatedType: string, relatedId: string) => {
    setNotifications(prev => prev.filter(n => !(n.relatedType === relatedType && n.relatedId === relatedId)));
    db.notifications
      .filter(n => n.relatedType === relatedType && n.relatedId === relatedId)
      .delete()
      .catch(e => console.error('Bildirim silinemedi:', e));
  }, []);

  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);

  return (
    <ToastContext.Provider value={{ showToast, notifications, unreadCount, markAllRead, clearNotificationsByRelated }}>
      {children}
      <div className="fixed bottom-6 right-6 z-[300] flex flex-col gap-2 items-end pointer-events-none">
        {toasts.map(toast => (
          <div
            key={toast.id}
            onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
            className={`${BG[toast.type]} text-white px-6 py-4 rounded-xl shadow-2xl shadow-black/50 flex items-center gap-3 transition-all duration-500 transform pointer-events-auto cursor-pointer ${toast.visible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}
          >
            <span className="material-symbols-outlined text-xl">{ICON[toast.type]}</span>
            <span className="text-xs font-medium">{toast.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
